import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import {
  Activity,
  Users,
  Trophy,
  Share2,
  Award,
  MapPin,
  ChevronRight,
  Zap,
  Target,
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3ec18717-9700-48f1-a5a2-71ac0edd3a6f/artifacts/29waugc4_WhatsApp%20Image%202026-03-06%20at%2016.26.44.jpeg';
const HERO_IMAGE = 'https://images.pexels.com/photos/8007076/pexels-photo-8007076.jpeg';

function HeroImageCarousel() {
  const [activeIdx, setActiveIdx] = React.useState(0);
  const images = [
    { src: LOGO_URL, alt: 'LoveAll Logo', className: 'object-contain p-12 bg-card' },
    { src: HERO_IMAGE, alt: 'Badminton Action', className: 'object-cover' },
  ];
  React.useEffect(() => {
    const t = setInterval(() => setActiveIdx(i => (i + 1) % images.length), 3000);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <div className="relative rounded-2xl shadow-2xl w-full aspect-[4/3] overflow-hidden">
      {images.map((img) => (
        <img key={img.src} src={img.src} alt={img.alt} className={`absolute inset-0 w-full h-full transition-opacity duration-700 ${img.className} ${images.indexOf(img) === activeIdx ? 'opacity-100' : 'opacity-0'}`} />
      ))}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((img, i) => <div key={`dot-${img.src}`} className={`h-2 w-2 rounded-full transition-all ${i === activeIdx ? 'bg-white w-6' : 'bg-white/50'}`} />)}
      </div>
    </div>
  );
}

const features = [
  {
    icon: Activity,
    title: 'Live Match Scoring',
    description: 'Score matches point-by-point with a digital umpire interface. Track points, service side, faults, and game wins.',
    color: 'bg-primary',
  },
  {
    icon: Users,
    title: 'Player Statistics',
    description: 'Track individual performance with detailed stats, win/loss ratios, point streaks, and ELO-based ratings.',
    color: 'bg-secondary',
  },
  {
    icon: Trophy,
    title: 'Organize Tournaments',
    description: 'Create knockout, round robin, or group stage tournaments with automated brackets and real-time leaderboards.',
    color: 'bg-accent',
  },
  {
    icon: Share2,
    title: 'Live Sharing',
    description: 'Share live score links via WhatsApp or social media. Keep fans engaged with real-time updates.',
    color: 'bg-primary',
  },
  {
    icon: Award,
    title: 'Rankings & Awards',
    description: 'Automatic rankings with ELO ratings. Celebrate top performers with awards like "Smash King" and "Defense Wall".',
    color: 'bg-secondary',
  },
  {
    icon: MapPin,
    title: 'Venue Records',
    description: 'Maintain historical database of courts, venues, and head-to-head records.',
    color: 'bg-accent',
  },
];

function MobileLanding() {
  const navigate = useNavigate();
  React.useEffect(() => {
    // Mobile entry point is now QuickPlay (no friction)
    navigate('/play', { replace: true });
  }, [navigate]);
  return null;
}

export default function Landing() {
  const { quickDemo, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleQuickDemo = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quick-access/tournament_admin`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
      const data = await response.json();
      // JWT arrives as httpOnly cookie — only cache the profile
      localStorage.setItem('user', JSON.stringify(data.user));
      window.location.href = '/dashboard';
    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  // Mobile: show simplified Quick Match entry
  if (isMobile) {
    return <MobileLanding />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="LoveAll" className="h-14 w-14 rounded-full object-cover" />
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="nav-login-btn">Login</Button>
              </Link>
              <Button className="bg-primary hover:bg-primary/90" onClick={handleQuickDemo} disabled={loading} data-testid="nav-register-btn">
                {loading ? 'Loading...' : 'Get Started'}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center min-h-[70vh]">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-white/80 text-sm">
                <Zap className="h-4 w-4 text-accent" />
                The Ultimate Badminton Companion
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white uppercase tracking-tight leading-tight">
                Score. Track.<br />
                <span className="text-primary">Dominate.</span>
              </h1>
              <p className="text-lg text-white/70 max-w-lg">
                Professional-grade match scoring, player statistics, and tournament management for local badminton communities. Your entire badminton journey, in one app.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wide btn-hover"
                  onClick={handleQuickDemo}
                  disabled={loading}
                  data-testid="hero-demo-btn"
                >
                  {loading ? 'Loading...' : 'Get Started'}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Link to="/guest">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 font-bold uppercase tracking-wide"
                    data-testid="hero-guest-btn"
                  >
                    Quick Match — No Sign In
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/20 rounded-3xl blur-xl"></div>
                <HeroImageCarousel />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-foreground">
              Everything You Need
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              From casual games to professional tournaments, LoveAll has all the tools to elevate your badminton experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={feature.title}
                  className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-border bg-card"
                  data-testid={`feature-card-${index}`}
                >
                  <CardContent className="p-6">
                    <div className={`${feature.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-foreground mb-2 uppercase tracking-tight">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-primary-foreground mb-6">
            Ready to Play?
          </h2>
          <p className="text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Join thousands of badminton players who are already using LoveAll to track their games and improve their performance.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="bg-white text-primary hover:bg-white/90 font-bold uppercase tracking-wide btn-hover"
              onClick={handleQuickDemo}
              disabled={loading}
              data-testid="cta-demo-btn"
            >
              Try It Now
            </Button>
            <Link to="/register">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 font-bold uppercase tracking-wide"
                data-testid="cta-create-btn"
              >
                Create Account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-card border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="LoveAll" className="h-14 w-14 rounded-full object-cover" />
            </div>
            <p className="text-muted-foreground text-sm">
              &copy; 2026 LoveAll. All rights reserved. Let's Begin!
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
